import { SurfacePosition } from "./Designs";

// Multi-page offset support: Each frame/page can have its own offset
export interface SwatchOffsetPerPage {
  frameId: number; // productFrameId from ITrykProductFrame
  x: number;
  y: number;
}

// Support both legacy single offset and new multi-page offsets
export type SwatchOffsetData = TrykApi.Catalog.ITrykSwatchOffset | SwatchOffsetPerPage[];

export interface PrintScaleOption {
  ratio: string;
  percentage: string;
  value: number;
  thumbnailUrl: string;
}

export interface BasketItemState {
  id: string;
  layoutBrandId: number;
  productId: number;
  roomId: number;
}

export interface ColorwayState extends BasketItemState {
  colorway: TrykApi.Catalog.IColorway;
  installId: number;
  printScale: number;
  swatchOffset: SwatchOffsetData;
  textureId: number;
  areaRug: TrykApi.Catalog.ITrykAreaRugRequest;
  clutColorwayId?: string;
  rotation: number;
  position: SurfacePosition;
}

export interface RugState extends BasketItemState {
  rug: TrykApi.Catalog.IRug;
  printScale: number;
}

export type BasketItemStateType = ColorwayState | RugState;
export type BasketItemStateList = BasketItemStateType[];

export function isColorwayState(state: ColorwayState | RugState): state is ColorwayState {
  return (<ColorwayState>state).colorway !== undefined;
}

export function isRugState(state: ColorwayState | RugState): state is RugState {
  return (<RugState>state).rug !== undefined;
}

export interface BasketItem {
  id: string;
  display: BasketItemDisplay;
  brand: TrykApi.Catalog.ISiteBrand;
  product: TrykProductOption;
  room: RoomSceneOption;
}

export interface BasketItemColorway extends BasketItem {
  colorway: TrykApi.Catalog.IColorway;
  install: DesignInstallOption;
  printScale: PrintScaleOption;
  swatchOffset: SwatchOffsetData;
}

export interface BasketItemRug extends BasketItem {
  rug: TrykApi.Catalog.IRug;
  printScale: PrintScaleOption;
}

export type BasketItemType = BasketItemColorway | BasketItemRug;
export type BasketItemList = BasketItemType[];

export function isColorwayItem(item: BasketItemColorway | BasketItemRug): item is BasketItemColorway {
  return (<BasketItemColorway>item).colorway !== undefined;
}

export function isRugItem(item: BasketItemColorway | BasketItemRug): item is BasketItemRug {
  return (<BasketItemRug>item).rug !== undefined;
}

export type BasketItemDisplay = {
  primaryDisplay: string;
  secondaryDisplay: string;

  thumbnailUrl: string;
};

export interface TrykProductOption extends TrykApi.Catalog.ITrykProduct {
  thumbnailUrl: string;
  hasSwatch: boolean;
  hasRoom: boolean;
}

export interface RoomSceneOption extends TrykApi.Catalog.IRoomScene {
  thumbnailUrl: string;
}

export interface DesignInstallOption extends TrykApi.Catalog.IDesignInstall {
  thumbnailUrl: string;
}

export interface EmailDestinationOption extends TrykApi.Catalog.IEmailDestination {
  id: string;
}

export interface PhysicalDestinationOption extends TrykApi.Catalog.IPhysicalDestinationAddress {
  id: string;
}

export type AvailableItemOptions = {
  brands: TrykApi.Catalog.ISiteBrand[];
  installs: DesignInstallOption[];
  printScales: PrintScaleOption[];
  products: TrykProductOption[];
  rooms: RoomSceneOption[];
};

export interface ShippingOptions {
  emailDestinations: EmailDestinationOption[];
  physicalDestinations: PhysicalDestinationOption[];
}

// Utility functions for working with SwatchOffsetData
function isSwatchOffsetPerPage(obj: any): obj is SwatchOffsetPerPage {
  return (
    obj &&
    typeof obj.frameId === "number" &&
    typeof obj.x === "number" &&
    typeof obj.y === "number"
  );
}

export function isMultiPageOffset(offset: SwatchOffsetData): offset is SwatchOffsetPerPage[] {
  return offset !== null && Array.isArray(offset) && offset.every(isSwatchOffsetPerPage);
}

export function isSingleOffset(offset: SwatchOffsetData): offset is TrykApi.Catalog.ITrykSwatchOffset {
  return !Array.isArray(offset) && offset !== null && typeof offset === 'object';
}

export function getOffsetForFrame(offset: SwatchOffsetData, frameId: number): { x: number; y: number } {
  if (isMultiPageOffset(offset)) {
    const frameOffset = offset.find(o => o.frameId === frameId);
    return frameOffset ? { x: frameOffset.x, y: frameOffset.y } : { x: 0, y: 0 };
  }
  return offset || { x: 0, y: 0 };
}

// Resolves any SwatchOffsetData to the anchor (page 1) offset.
export function selectDefaultOffset(swatchOffset: SwatchOffsetData | null | undefined): { x: number; y: number } {
  if (!swatchOffset) {
    return { x: 0, y: 0 };
  }

  if (isMultiPageOffset(swatchOffset)) {
    return swatchOffset.length > 0 ? { x: swatchOffset[0].x, y: swatchOffset[0].y } : { x: 0, y: 0 };
  }

  return isSingleOffset(swatchOffset) ? swatchOffset : { x: 0, y: 0 };
}

// Multi-page swatch frames are displayed side by side, so only the position of the
// first page is user-editable; each subsequent page is offset from it by the
// cumulative width (at the current print scale) of the pages before it.
export function getSideBySideOffsets(
  baseOffset: { x: number; y: number },
  frames: { productFrameId: number; width: number }[],
  scaleValue: number
): SwatchOffsetPerPage[] {
  let cumulativeWidth = 0;

  return frames.map(frame => {
    const pageOffset: SwatchOffsetPerPage = {
      frameId: frame.productFrameId,
      x: baseOffset.x + cumulativeWidth,
      y: baseOffset.y
    };

    cumulativeWidth += frame.width * scaleValue;

    return pageOffset;
  });
}
